<script setup>
import { toRef } from 'vue';
import { useEvolutionReconnect } from 'dashboard/composables/useEvolutionReconnect';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';
import EvolutionPublicLinkShare from 'dashboard/components/EvolutionPublicLinkShare.vue';
import QrCodeAutoRefreshIndicator from 'dashboard/components/QrCodeAutoRefreshIndicator.vue';
import EvolutionNumberMismatchDialog from 'dashboard/components/EvolutionNumberMismatchDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const {
  showQrPanel,
  isLoadingQrcode,
  isPolling,
  qrcodeBase64,
  publicLinkUrl,
  secondsRemaining,
  needsManualRefresh,
  totalSeconds,
  requestAuthorization,
  refreshQrcode,
  activeMismatch,
  pendingMismatch,
  isResolvingMismatch,
  reviewMismatch,
  closeMismatch,
  acceptNumberChange,
  declineNumberChange,
} = useEvolutionReconnect(toRef(props, 'inbox'));
</script>

<template>
  <div class="mx-6">
    <InboxReconnectionRequired
      v-if="pendingMismatch && !showQrPanel"
      :action-label="
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.REVIEW')
      "
      :description="
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.BANNER', {
          scanned: `+${pendingMismatch.phone_number}`,
        })
      "
      @reauthorize="reviewMismatch"
    />
    <InboxReconnectionRequired
      v-else-if="!showQrPanel"
      :action-label="
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.RECONNECT.ACTION_LABEL')
      "
      :description="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.RECONNECT.DESC')"
      @reauthorize="requestAuthorization"
    />

    <div v-else class="flex flex-col items-center gap-4 py-4 text-center">
      <img
        v-if="qrcodeBase64"
        :src="qrcodeBase64"
        :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.TITLE')"
        class="size-56 rounded-lg border border-n-weak"
      />
      <p v-if="isPolling" class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR.WAITING') }}
      </p>
      <EvolutionPublicLinkShare v-if="publicLinkUrl" :url="publicLinkUrl" />
      <QrCodeAutoRefreshIndicator
        :seconds-remaining="secondsRemaining"
        :total-seconds="totalSeconds"
        :needs-manual-refresh="needsManualRefresh"
        :is-loading="isLoadingQrcode"
        @refresh="refreshQrcode"
      />
    </div>
    <EvolutionNumberMismatchDialog
      :inbox="inbox"
      :mismatch="activeMismatch"
      :is-loading="isResolvingMismatch"
      @accept="acceptNumberChange"
      @decline="declineNumberChange"
      @close="closeMismatch"
    />
  </div>
</template>
